import React, { useEffect, useState } from 'react';
import authService from '../../services/authService';

const UserBilling = () => {
  const [balance, setBalance] = useState(null);
  const [ledger, setLedger] = useState({ total: 0, page: 1, pageSize: 25, items: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [prefs, setPrefs] = useState({ PreferredTimezone: '' });
  const [rangeOpen, setRangeOpen] = useState(false);
  const [startDateStr, setStartDateStr] = useState('');
  const [endDateStr, setEndDateStr] = useState('');
  const [appliedStartIso, setAppliedStartIso] = useState(null);
  const [appliedEndIso, setAppliedEndIso] = useState(null);
  const [rangeError, setRangeError] = useState('');
  const [recentIncome, setRecentIncome] = useState([]);

  const loadBalance = async () => {
    try {
      const headers = authService.getAuthHeaders();
      const r = await fetch('/api/billing/balance', { credentials: 'include', headers });
      if (r.ok) setBalance(await r.json());
    } catch {}
  };

  const loadLedger = async (page = 1, pageSize = 25, startIsoOverride = undefined, endIsoOverride = undefined) => {
    try {
      const headers = authService.getAuthHeaders();
      const qs = new URLSearchParams();
      qs.set('page', String(page));
      qs.set('pageSize', String(pageSize));
      const startToUse = (startIsoOverride !== undefined) ? startIsoOverride : appliedStartIso;
      const endToUse = (endIsoOverride !== undefined) ? endIsoOverride : appliedEndIso;
      if (startToUse) qs.set('start', startToUse);
      if (endToUse) qs.set('end', endToUse);
      const r = await fetch(`/api/billing/ledger?${qs.toString()}`, { credentials: 'include', headers });
      if (r.ok) setLedger(await r.json());
    } catch {}
  };

  useEffect(() => { (async () => { 
    try { 
      setLoading(true); 
      const t = authService.getToken && authService.getToken();
      const r = await fetch('/api/userpreferences', { headers: t ? { Authorization: `Bearer ${t}` } : {}, credentials: 'include' });
      if (r.ok) {
        const p = await r.json();
        setPrefs({ PreferredTimezone: p?.PreferredTimezone || '' });
      }
      // No currency rates needed as amounts remain in USD
      await Promise.all([loadBalance(), loadLedger(1, 25)]);
      try {
        const ti = authService.getToken && authService.getToken();
        const ri = await fetch('/api/billing/income/recent?take=10', { credentials:'include', headers: ti ? { Authorization: `Bearer ${ti}` } : {} });
        if (ri.ok) setRecentIncome(await ri.json());
      } catch {}
    } catch (e) { setError(e.message||'Failed'); } finally { setLoading(false);} })(); }, []);

  const displayAmount = (cents) => `$${((cents||0)/100).toFixed(2)}`;

  const getPartsInTz = (dateUtc, tz) => {
    try {
      const fmt = new Intl.DateTimeFormat('en-US', { timeZone: tz || 'UTC', hour12:false, year:'numeric', month:'2-digit', day:'2-digit', hour:'2-digit', minute:'2-digit', second:'2-digit' });
      const parts = fmt.formatToParts(dateUtc);
      const map = Object.fromEntries(parts.map(p => [p.type, p.value]));
      return {
        y: parseInt(map.year,10), m: parseInt(map.month,10), d: parseInt(map.day,10),
        hh: parseInt(map.hour,10), mm: parseInt(map.minute,10), ss: parseInt(map.second,10)
      };
    } catch { return { y: dateUtc.getUTCFullYear(), m: dateUtc.getUTCMonth()+1, d: dateUtc.getUTCDate(), hh: dateUtc.getUTCHours(), mm: dateUtc.getUTCMinutes(), ss: dateUtc.getUTCSeconds() }; }
  };
  const daysBetween = (y1,m1,d1,y2,m2,d2) => {
    const a = Date.UTC(y1, m1-1, d1, 0,0,0,0);
    const b = Date.UTC(y2, m2-1, d2, 0,0,0,0);
    return Math.round((a - b)/(24*60*60*1000));
  };
  const zonedMidnightUtcIso = (ymd, tz) => {
    if (!ymd) return null;
    const [y,m,d] = ymd.split('-').map(n=>parseInt(n,10));
    if (!y||!m||!d) return null;
    // Use a stable UTC base (noon) then adjust to local midnight
    const base = new Date(Date.UTC(y, m-1, d, 12, 0, 0));
    const p = getPartsInTz(base, tz);
    const deltaDays = -daysBetween(y,m,d, p.y,p.m,p.d); // how many days to move to reach selected local date
    const localMillis = (p.hh*3600 + p.mm*60 + p.ss) * 1000;
    const resultMs = base.getTime() - localMillis + (deltaDays*24*60*60*1000);
    return new Date(resultMs).toISOString();
  };
  const zonedEndOfDayUtcIso = (ymd, tz) => {
    // next local midnight minus 1 ms
    if (!ymd) return null;
    const [y,m,d] = ymd.split('-').map(n=>parseInt(n,10));
    const nextDay = new Date(Date.UTC(y, m-1, d, 12, 0, 0));
    nextDay.setUTCDate(nextDay.getUTCDate() + 1);
    const nextIso = zonedMidnightUtcIso(`${nextDay.getUTCFullYear()}-${String(nextDay.getUTCMonth()+1).padStart(2,'0')}-${String(nextDay.getUTCDate()).padStart(2,'0')}`, tz);
    if (!nextIso) return null;
    return new Date(new Date(nextIso).getTime() - 1).toISOString();
  };
  const validateRange = (sIso, eIso) => {
    setRangeError('');
    if (!sIso || !eIso) return true;
    const s = new Date(sIso);
    const e = new Date(eIso);
    if (e < s) { setRangeError('End date must be on/after start date.'); return false; }
    const days = Math.floor((e - s) / (24*60*60*1000)) + 1;
    if (days > 30) { setRangeError('Select a range of up to 30 days.'); return false; }
    return true;
  };

  const parseUtcDate = (val) => {
    if (!val) return new Date(NaN);
    if (val instanceof Date) return val;
    const s = String(val);
    const hasTz = /Z$/i.test(s) || /[+\-]\d{2}:?\d{2}$/.test(s);
    return new Date(hasTz ? s : s + 'Z');
  };

  const formatDate = (iso) => {
    const dt = parseUtcDate(iso);
    try {
      if (prefs.PreferredTimezone) {
        return new Intl.DateTimeFormat(undefined, {
          timeZone: prefs.PreferredTimezone,
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', second: '2-digit',
          hour12: false,
          timeZoneName: 'short'
        }).format(dt);
      }
      return dt.toLocaleString();
    } catch { return dt.toLocaleString(); }
  };

  const timezoneHint = (() => {
    try {
      const tz = prefs.PreferredTimezone || (Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
      const now = new Date();
      let off = 'UTC';
      try {
        const s = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset', hour: '2-digit' }).format(now);
        const m = s.match(/GMT([+\-]\d{1,2})(?::(\d{2}))?/);
        off = m ? `UTC${m[1]}${m[2] ? ':' + m[2] : ''}` : 'UTC';
      } catch {}
      const city = tz.split('/').slice(-1)[0].replace(/_/g, ' ');
      return `${city} (${off})${prefs.PreferredTimezone ? '' : ' • system default'}`;
    } catch { return 'UTC'; }
  })();

  return (
    <div className="auth-container">
      <div className="auth-card" style={{ maxWidth: 1200 }}>
        <h2 style={{ marginTop: 0 }}>Billing</h2>
        {error && <div className="error-message" style={{ marginBottom: 8 }}>{error}</div>}
        <div style={{ display:'flex', gap: 16, flexWrap:'nowrap', marginBottom: 12, fontSize: '1.0rem' }}>
          <div style={{ whiteSpace:'nowrap' }}><strong>Balance:</strong> {balance ? displayAmount(balance.balanceCents) : '—'}</div>
          <div style={{ whiteSpace:'nowrap' }}><strong>Today:</strong> {balance ? displayAmount(balance.spendTodayCents) : '—'}</div>
          <div style={{ color:'#64748b', whiteSpace:'nowrap' }}><strong>Timezone:</strong> {timezoneHint}</div>
        </div>
        <div style={{ color:'#64748b', fontSize: 12, marginTop: -8, marginBottom: 12 }}>All transactions are recorded and settled in USD.</div>
        <div style={{ marginBottom: 8, display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
          <div style={{ position:'relative' }}>
            <button onClick={()=>setRangeOpen(v=>!v)}>Date Range</button>
            {(appliedStartIso || appliedEndIso) && (
              <span style={{ marginLeft: 8, fontSize:12, color:'#64748b' }}>
                {startDateStr || '—'} {startDateStr||endDateStr ? '→' : ''} {endDateStr || '—'}
              </span>
            )}
            {rangeOpen && (
              <div style={{ position:'absolute', zIndex: 1000, top: '110%', left: 0, background:'#fff', border:'1px solid #e5e7eb', borderRadius:8, boxShadow:'0 8px 20px rgba(0,0,0,0.12)', padding:12, minWidth: 280 }}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                  <label style={{ fontSize:12, color:'#374151' }}>Start
                    <input type="date" value={startDateStr} onChange={(e)=>setStartDateStr(e.target.value)} />
                  </label>
                  <label style={{ fontSize:12, color:'#374151' }}>End
                    <input type="date" value={endDateStr} onChange={(e)=>setEndDateStr(e.target.value)} />
                  </label>
                </div>
                {rangeError && <div style={{ color:'#b91c1c', fontSize:12, marginTop:6 }}>{rangeError}</div>}
                <div style={{ display:'flex', justifyContent:'flex-end', gap:8, marginTop:10 }}>
                  <button onClick={()=>{ setRangeOpen(false); }}>Cancel</button>
                  <button onClick={async()=>{
                    const tz = prefs.PreferredTimezone || (Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
                    const sIso = zonedMidnightUtcIso(startDateStr, tz);
                    const eIso = zonedEndOfDayUtcIso(endDateStr, tz);
                    if (!validateRange(sIso, eIso)) return;
                    setAppliedStartIso(sIso);
                    setAppliedEndIso(eIso);
                    setRangeOpen(false);
                    await loadLedger(1, ledger.pageSize||25, sIso, eIso);
                  }} className="primary">Apply</button>
                  <button onClick={async()=>{
                    setStartDateStr(''); setEndDateStr(''); setAppliedStartIso(null); setAppliedEndIso(null); setRangeError(''); setRangeOpen(false);
                    await loadLedger(1, ledger.pageSize||25, null, null);
                  }}>Clear</button>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="table-scroll">
          <table className="admin-table">
            <thead><tr><th>Date</th><th>Provider</th><th>Model</th><th>In</th><th>Out</th><th>Cost</th><th>Balance</th><th>Reason</th></tr></thead>
            <tbody>
              {(ledger.items||[]).map(i => (
                <tr key={i.id}>
                  <td>{formatDate(i.occurredAt)}</td>
                  <td>{i.provider||''}</td>
                  <td>{i.model||''}</td>
                  <td>{i.inputTokens??''}</td>
                  <td>{i.outputTokens??''}</td>
                  <td>{displayAmount(i.costCents)}</td>
                  <td>{i.balanceAfterCents!=null ? displayAmount(i.balanceAfterCents) : '—'}</td>
                  <td>{i.reason||''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Recent Payments */}
        <div className="dashboard-card" style={{ marginTop: 12 }}>
          <h3 style={{ marginTop: 0 }}>Recent Payments</h3>
          {recentIncome.length === 0 ? (
            <div style={{ color:'#64748b', fontSize:12 }}>No recent payments.</div>
          ) : (
            <div className="table-scroll">
              <table className="admin-table">
                <thead><tr><th>Date</th><th>Amount</th><th>Currency</th><th>Package</th><th>Reference</th></tr></thead>
                <tbody>
                  {recentIncome.map((r, idx) => (
                    <tr key={idx}>
                      <td>{formatDate(r.occurredAt)}</td>
                      <td>{displayAmount(r.amountCents)}</td>
                      <td>{(r.currency||'USD').toUpperCase()}</td>
                      <td>{r.packageId||''}</td>
                      <td style={{ maxWidth: 220, overflow:'hidden', textOverflow:'ellipsis' }}>{r.correlationId||''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="admin-pager" style={{ marginTop: 8 }}>
          <button disabled={ledger.page<=1} onClick={()=>loadLedger(ledger.page-1, ledger.pageSize)}>Prev</button>
          <span>Page {ledger.page} of {Math.max(1, Math.ceil((ledger.total||0)/(ledger.pageSize||25)))}</span>
          <button disabled={ledger.page >= Math.ceil((ledger.total||0)/(ledger.pageSize||25))} onClick={()=>loadLedger(ledger.page+1, ledger.pageSize)}>Next</button>
        </div>
      </div>
    </div>
  );
};

export default UserBilling;


